import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus,
    Image as ImageIcon,
    Video as VideoIcon,
    Eye,
    RefreshCw,
    Trash2,
    X,
    Upload,
    CheckCircle2,
    AlertCircle,
    PlayCircle,
    ChevronRight,
    Filter,
    Maximize2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminLayout from "../components/admin/AdminLayout";
import { useUpload, useGallery } from "../utils/GalleryUseUpload";
import { useAdminPermissions } from "../hooks/useAdminPermissions";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

// Video processing indicator component
const VideoProcessingIndicator = ({ item, onStatusCheck }) => {
    const [secondsLeft, setSecondsLeft] = useState(45);
    const [isChecking, setIsChecking] = useState(false);
    const pollIntervalRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    useEffect(() => {
        // Only run if video is still processing
        if (item.url) {
            // Video is ready, stop everything
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            return;
        }

        // Poll every 5 seconds
        pollIntervalRef.current = setInterval(async () => {
            const updated = await onStatusCheck(item.id);
            if (updated?.url) {
                clearInterval(pollIntervalRef.current);
                clearInterval(countdownIntervalRef.current);
            }
        }, 5000);

        // Countdown timer
        countdownIntervalRef.current = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    clearInterval(pollIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [item.id]); // Only re-run if item.id changes (i.e., different video)

    const handleManualCheck = async () => {
        setIsChecking(true);
        await onStatusCheck(item.id);
        setIsChecking(false);
    };

    const progress = ((45 - secondsLeft) / 45) * 100;

    return (
        <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-4 text-center">
            <div className="w-16 h-16 mb-4 relative">
                <svg className="transform -rotate-90 w-16 h-16">
                    <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="rgba(139, 92, 246, 0.2)"
                        strokeWidth="6"
                        fill="none"
                    />
                    <circle
                        cx="32"
                        cy="32"
                        r="28"
                        stroke="#62397f"
                        strokeWidth="6"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
                        className="transition-all duration-300"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <VideoIcon size={24} className="text-[#62397f]" />
                </div>
            </div>
            <p className="text-white text-sm font-semibold mb-1">Processing Video</p>
            <p className="text-white/60 text-xs mb-3">{secondsLeft}s remaining</p>
            <button
                onClick={handleManualCheck}
                disabled={isChecking}
                className="px-4 py-2 bg-[#62397f] hover:bg-[#7C3AED] disabled:bg-[#62397f]/50 text-white text-xs rounded-lg transition-all active:scale-95"
            >
                {isChecking ? "Checking..." : "Check Status"}
            </button>
        </div>
    );
};

function AdminGalleryPage() {
    const { checkPermission } = useAdminPermissions();
    const [upload, { loading: isUploading }] = useUpload();
    const {
        items: galleryItems,
        loading: isLoading,
        createItem,
        replaceItem,
        deleteItem,
        refresh,
        checkStatus,
    } = useGallery();
    const [filter, setFilter] = useState("all");
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [isReplacing, setIsReplacing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);




    // Handlers
    const handleUpload = async (files) => {
        if (!checkPermission("admin", "upload gallery items")) return;
        if (!files || files.length === 0) return;

        for (const file of files) {
            try {
                const type = file.type.startsWith("image/")
                    ? "image"
                    : file.type.startsWith("video/")
                        ? "video"
                        : null;

                if (!type) {
                    toast.error(`${file.name} is an unsupported file type`);
                    continue;
                }

                if (isReplacing && type !== selectedMedia.type) {
                    toast.error(
                        `Please upload a ${selectedMedia.type} to replace the current file`
                    );
                    continue;
                }

                if (isReplacing) {
                    // Send file directly to the PUT endpoint via replaceItem
                    const fd = new FormData();
                    fd.append("file", file);
                    await replaceItem(selectedMedia.id, fd);
                    toast.success("Media replaced successfully");
                    setIsReplacing(false);
                } else {
                    // upload() now creates the DB record via /admin/gallery/upload
                    const result = await upload({ file });

                    if (result?.error) {
                        toast.error(`Failed to upload ${file.name}: ${result.error}`);
                        continue;
                    }

                    // Refresh the gallery list to include the new item
                    await refresh();
                    toast.success(
                        result.status === "processing"
                            ? "Video uploaded — processing in background"
                            : "Media uploaded successfully"
                    );
                }

                setIsUploadModalOpen(false);
            } catch (error) {
                console.error("Upload failed:", error);
                toast.error(`Failed to upload ${file.name}`);
            }
        }
    };



    const openReplace = (item) => {
        if (!checkPermission("admin", "replace media items")) return;
        setSelectedMedia(item);
        setIsReplacing(true);
        setIsUploadModalOpen(true);
    };

    const openDelete = (item) => {
        if (!checkPermission("admin", "delete media items")) return;
        setSelectedMedia(item);
        setIsDeleteModalOpen(true);
    };

    const openView = (item) => {
        setSelectedMedia(item);
        setIsViewModalOpen(true);
    };

    return (
        <AdminLayout>
            <Toaster position="top-right" richColors />

            <div className="p-4 md:p-8 max-w-7xl mx-auto">
                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-[#111111] dark:text-white font-poppins">
                            Gallery
                        </h1>
                        <p className="text-[#6B7280] dark:text-[#A0A0A0] text-sm md:text-base font-inter">
                            Images and videos uploaded for display
                        </p>
                    </div>

                    <button
                        onClick={() => {
                            setIsReplacing(false);
                            setIsUploadModalOpen(true);
                        }}
                        className="flex items-center justify-center gap-2 bg-[#62397f] hover:bg-[#7C3AED] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-[#62397f]/20 active:scale-95 group"
                    >
                        <Plus
                            size={20}
                            className="group-hover:rotate-90 transition-transform duration-300"
                        />
                        <span>Upload Media</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
                    {["all", "image", "video"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 capitalize whitespace-nowrap ${filter === f
                                ? "bg-[#62397f] text-white shadow-md shadow-[#62397f]/20"
                                : "bg-white dark:bg-[#1A1A1A] text-[#666666] dark:text-[#A0A0A0] border border-[#E5E7EB] dark:border-[#2A2A2A] hover:border-[#62397f] dark:hover:border-[#62397f]"
                                }`}
                        >
                            {f === "all" ? "All Media" : f + "s"}
                        </button>
                    ))}
                </div>

                {/* Gallery Content */}
                {(() => {
                    const safeItems = Array.isArray(galleryItems) ? galleryItems : [];
                    const filteredItems = filter === "all"
                        ? safeItems
                        : safeItems.filter((item) => item.type === filter);

                    if (isLoading) {
                        return (
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                                {[...Array(8)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="aspect-square bg-gray-200 dark:bg-[#1A1A1A] rounded-2xl animate-pulse shadow-sm relative overflow-hidden"
                                    >
                                        {/* Type Badge Placeholder */}
                                        <div className="absolute top-3 left-3 w-12 h-4 bg-gray-300 dark:bg-[#262626] rounded-lg" />

                                        {/* Info Bar Placeholder (Top on Mobile, Bottom on Desktop) */}
                                        <div className="absolute inset-x-0 h-8 bg-gradient-to-b from-gray-300/50 to-transparent md:hidden" />
                                        <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-gray-300/50 to-transparent hidden md:block" />
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    if (safeItems.length === 0) {
                        return (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex flex-col items-center justify-center py-20 text-center"
                            >
                                <div className="w-24 h-24 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-6">
                                    <ImageIcon size={40} className="text-[#62397f]" />
                                </div>
                                <h3 className="text-xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
                                    No gallery items yet
                                </h3>
                                <p className="text-[#6B7280] dark:text-[#A0A0A0] max-w-sm mb-8 font-inter">
                                    Upload images or videos to showcase your rentals and activities.
                                </p>
                                <button
                                    onClick={() => setIsUploadModalOpen(true)}
                                    className="bg-[#62397f] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#7C3AED] transition-all duration-200 shadow-lg active:scale-95"
                                >
                                    Upload Media
                                </button>
                            </motion.div>
                        );
                    }

                    if (filteredItems.length === 0) {
                        return (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex flex-col items-center justify-center py-20 text-center"
                            >
                                <div className="w-24 h-24 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-6">
                                    {filter === "video" ? (
                                        <VideoIcon size={40} className="text-[#62397f]" />
                                    ) : (
                                        <ImageIcon size={40} className="text-[#62397f]" />
                                    )}
                                </div>
                                <h3 className="text-xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
                                    No {filter}s found
                                </h3>
                                <p className="text-[#6B7280] dark:text-[#A0A0A0] max-w-sm mb-8 font-inter">
                                    Upload {filter === "video" ? "a video" : "an image"} to see it here, or switch to a different filter.
                                </p>
                                <button
                                    onClick={() => setFilter("all")}
                                    className="bg-[#62397f] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#7C3AED] transition-all duration-200 shadow-lg active:scale-95"
                                >
                                    Show All Media
                                </button>
                            </motion.div>
                        );
                    }

                    return (
                        <motion.div
                            key={filter}
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
                        >
                            {filteredItems.map((item) => (
                                <motion.div
                                    key={item.id}
                                    variants={itemVariants}
                                    className="group relative aspect-square bg-white dark:bg-[#1A1A1A] rounded-2xl overflow-hidden border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm hover:shadow-xl transition-shadow duration-300"
                                >
                                    {/* Thumbnail */}
                                    {item.type === "video" ? (
                                        <div className="relative w-full h-full">
                                            <video
                                                src={item.url}
                                                className="w-full h-full object-cover"
                                                muted
                                                onMouseEnter={(e) => {
                                                    const playPromise = e.target.play();
                                                    if (playPromise !== undefined) {
                                                        playPromise.catch(() => {
                                                            // Auto-play was prevented
                                                        });
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.pause();
                                                    e.target.currentTime = 0;
                                                }}
                                            />
                                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
                                                <PlayCircle size={48} className="text-white opacity-80" />
                                            </div>
                                        </div>
                                    ) : (
                                        <img
                                            src={item.url}
                                            alt="Gallery"
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                    )}

                                    {/* Badges */}
                                    <div className="absolute top-3 left-3 flex flex-col gap-2">
                                        <div
                                            className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider backdrop-blur-md ${item.type === "video"
                                                ? "bg-amber-500/80 text-white"
                                                : "bg-blue-500/80 text-white"
                                                }`}
                                        >
                                            {item.type}
                                        </div>
                                    </div>

                                    {/* Actions Overlay */}
                                    <div className="absolute inset-0 bg-black/40 transition-opacity duration-300 flex z-10 items-center justify-center gap-3 w-full h-full
                                        md:opacity-0 md:group-hover:opacity-100
                                        max-md:bg-transparent max-md:pointer-events-none
                                    ">
                                        <button
                                            onClick={() => openView(item)}
                                            className="p-2.5 md:p-3 bg-white md:hover:bg-[#62397f] text-[#62397f] md:hover:text-white rounded-lg md:rounded-xl transition-shadow duration-200 shadow-lg pointer-events-auto flex items-center justify-center"
                                            title="View"
                                        >
                                            <Eye className="w-4 h-4 md:w-5 md:h-5" />
                                        </button>
                                        <button
                                            onClick={() => openReplace(item)}
                                            className="p-2.5 md:p-3 bg-white md:hover:bg-[#62397f] text-[#62397f] md:hover:text-white rounded-lg md:rounded-xl transition-shadow duration-200 shadow-lg pointer-events-auto flex items-center justify-center"
                                            title="Replace"
                                        >
                                            <RefreshCw className="w-4 h-4 md:w-5 md:h-5" />
                                        </button>
                                        <button
                                            onClick={() => openDelete(item)}
                                            className="p-2.5 md:p-3 bg-white md:hover:bg-red-500 text-red-500 md:hover:text-white rounded-lg md:rounded-xl transition-shadow duration-200 shadow-lg pointer-events-auto flex items-center justify-center"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                                        </button>
                                    </div>

                                    {/* Info Bar */}
                                    <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                        <p className="text-white text-[10px] font-medium opacity-80">
                                            Uploaded {new Date(item.created_at).toLocaleDateString()}
                                        </p>
                                    </div>

                                    {/* Video Processing Overlay */}
                                    {item.type === "video" && !item.url && (
                                        <VideoProcessingIndicator item={item} onStatusCheck={checkStatus} />
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    );
                })()}
            </div>

            {/* Upload Modal */}
            <AnimatePresence>
                {isUploadModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsUploadModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative w-full max-w-lg bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl overflow-hidden z-10"
                        >
                            <div className="p-6 border-b border-[#E5E7EB] dark:border-[#2A2A2A] flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
                                        {isReplacing ? "Replace Media" : "Upload Media"}
                                    </h2>
                                    <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0] font-inter">
                                        {isReplacing
                                            ? "Overwrite the existing file"
                                            : "Add new files to your gallery"}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsUploadModalOpen(false)}
                                    className="p-2 hover:bg-[#F3F4F6] dark:hover:bg-[#262626] rounded-full transition-colors"
                                >
                                    <X size={20} className="text-[#6B7280] dark:text-[#A0A0A0]" />
                                </button>
                            </div>

                            <div className="p-8">
                                {isReplacing && (
                                    <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-xl flex gap-3">
                                        <AlertCircle
                                            className="text-amber-500 shrink-0"
                                            size={20}
                                        />
                                        <p className="text-sm text-amber-700 dark:text-amber-400 font-inter">
                                            Replacing this media will permanently overwrite the
                                            existing file. Must be the same type:{" "}
                                            <span className="font-bold uppercase">
                                                {selectedMedia.type}
                                            </span>
                                            .
                                        </p>
                                    </div>
                                )}

                                <label
                                    className={`
                    group flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-10 transition-all duration-200 cursor-pointer
                    ${isUploading ? "bg-gray-50 dark:bg-[#111111] border-gray-200 pointer-events-none" : "border-[#E5E7EB] dark:border-[#2A2A2A] hover:border-[#62397f] hover:bg-[#F5F3FF] dark:hover:bg-[#2A2440]"}
                    `}
                                >
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept={
                                            isReplacing
                                                ? selectedMedia.type === "video"
                                                    ? "video/*"
                                                    : "image/*"
                                                : "image/*,video/*"
                                        }
                                        multiple={!isReplacing}
                                        onChange={(e) => handleUpload(e.target.files)}
                                    />

                                    <div
                                        className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all duration-200 ${isUploading ? "bg-gray-100 dark:bg-[#262626]" : "bg-[#F5F3FF] dark:bg-[#2A2440] group-hover:scale-110"}`}
                                    >
                                        {isUploading ? (
                                            <RefreshCw
                                                size={32}
                                                className="text-[#62397f] animate-spin"
                                            />
                                        ) : (
                                            <Upload size={32} className="text-[#62397f]" />
                                        )}
                                    </div>

                                    <div className="text-center">
                                        <p className="text-lg font-bold text-[#111111] dark:text-white mb-1 font-poppins">
                                            {isUploading
                                                ? "Uploading..."
                                                : "Click to upload or drag & drop"}
                                        </p>
                                        <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0] font-inter">
                                            JPG, JPEG, PNG, MP4 (Max 50MB)
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* View Modal */}
            <AnimatePresence>
                {isViewModalOpen && selectedMedia && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsViewModalOpen(false)}
                            className="absolute inset-0 bg-black/95 backdrop-blur-md"
                        />

                        <motion.button
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            onClick={() => setIsViewModalOpen(false)}
                            className="absolute top-6 right-6 z-10 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all"
                        >
                            <X size={24} />
                        </motion.button>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="relative w-full h-full max-w-6xl max-h-[85vh] p-4 flex items-center justify-center"
                        >
                            {selectedMedia.type === "video" ? (
                                <video
                                    src={selectedMedia.url}
                                    controls
                                    autoPlay
                                    className="max-w-full max-h-full rounded-xl shadow-2xl"
                                />
                            ) : (
                                <div className="relative group overflow-auto max-w-full max-h-full">
                                    <img
                                        src={selectedMedia.url}
                                        alt="Gallery Full"
                                        className="max-w-full max-h-full object-contain rounded-xl shadow-2xl transition-transform duration-300"
                                    />
                                </div>
                            )}

                            <div className="absolute bottom-6 inset-x-0 text-center">
                                <div className="inline-flex flex-col items-center bg-black/40 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/10">
                                    <p className="text-white font-semibold font-poppins">
                                        {selectedMedia.type.toUpperCase()}
                                    </p>
                                    <p className="text-white/60 text-sm font-inter">
                                        Uploaded on{" "}
                                        {new Date(selectedMedia.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {isDeleteModalOpen && selectedMedia && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="relative w-full max-w-md bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl p-8 z-10"
                        >
                            <div className="w-16 h-16 bg-red-50 dark:bg-red-900/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                                <Trash2 size={32} className="text-red-500" />
                            </div>

                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
                                    Delete Media?
                                </h2>
                                <p className="text-[#6B7280] dark:text-[#A0A0A0] font-inter">
                                    This media will be permanently removed from the gallery. This
                                    action cannot be undone.
                                </p>
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="flex-1 px-6 py-3 bg-[#F3F4F6] dark:bg-[#262626] text-[#666666] dark:text-[#A0A0A0] rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-[#333333] transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        if (isDeleting) return;

                                        try {
                                            setIsDeleting(true);

                                            await deleteItem(selectedMedia.id);

                                            toast.success("Media deleted successfully");
                                            setIsDeleteModalOpen(false);
                                            setSelectedMedia(null);
                                        } catch (error) {
                                            console.error("Delete failed:", error);
                                            toast.error("Failed to delete media");
                                        } finally {
                                            setIsDeleting(false);
                                        }
                                    }}
                                    disabled={isDeleting}
                                    className="flex-1 px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all shadow-lg shadow-red-500/20 active:scale-95 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {isDeleting ? (
                                        <RefreshCw size={20} className="animate-spin" />
                                    ) : (
                                        "Delete"
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <style jsx="true" global="true">{`
            @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
            }
            .animate-pulse {
            background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s infinite;
            }
            .dark .animate-pulse {
            background: linear-gradient(90deg, #262626 25%, #333333 50%, #262626 75%);
            background-size: 200% 100%;
            }
            .scrollbar-hide::-webkit-scrollbar {
            display: none;
            }
            .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
            }
        `}</style>
        </AdminLayout>
    );
}

export default AdminGalleryPage;
